export type BootstrapTask = () => Promise<boolean>;

export interface BootstrapGameOptions {
  ensureSession: BootstrapTask;
  loadCanPlayAgain: BootstrapTask;
  isActive?: () => boolean;
  retryDelayMs?: number;
  attempts?: number;
  delay?: (delayMs: number) => Promise<void>;
}

export const GAME_BOOTSTRAP_ATTEMPTS = 2;
export const GAME_BOOTSTRAP_RETRY_DELAY_MS = 250;

const waitFor = (delayMs: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, delayMs);
  });

const runWithRetry = async (
  task: BootstrapTask,
  attemptsLeft: number,
  retryDelayMs: number,
  delay: (delayMs: number) => Promise<void>,
  isActive: () => boolean
): Promise<boolean> => {
  if (attemptsLeft === 0 || !isActive()) {
    return false;
  }
  if (await task()) {
    return true;
  }
  if (attemptsLeft === 1 || !isActive()) {
    return false;
  }

  await delay(retryDelayMs);
  return runWithRetry(task, attemptsLeft - 1, retryDelayMs, delay, isActive);
};

export const bootstrapGame = async ({
  ensureSession,
  loadCanPlayAgain,
  isActive = () => true,
  retryDelayMs = GAME_BOOTSTRAP_RETRY_DELAY_MS,
  attempts = GAME_BOOTSTRAP_ATTEMPTS,
  delay = waitFor,
}: BootstrapGameOptions) => {
  const hasSession = await runWithRetry(
    ensureSession,
    attempts,
    retryDelayMs,
    delay,
    isActive
  );
  if (!hasSession || !isActive()) {
    return false;
  }

  return runWithRetry(
    loadCanPlayAgain,
    attempts,
    retryDelayMs,
    delay,
    isActive
  );
};
